import type { AxiosResponse } from 'axios';
import midasInstance from './midasInstance';

interface PostMidasFlowParams<TVariables = Record<string, unknown>> {
  /** Name of the REC callflow to trigger (e.g. `'communicator-translation'`). */
  callflow: string;
  /** Flow-specific variables forwarded to the callflow. */
  variables: TVariables;
}

/**
 * Posts a callflow to the Midas REC trigger endpoint.
 *
 * Use this as the shared primitive for initiating any Midas callflow from RSP.
 * `blocking: true` and auth retry logic are applied automatically via the
 * underlying `midasInstance` interceptors.
 *
 * @param params - Callflow name and its variables.
 * @returns Axios response typed to the expected flow response shape.
 *
 * @example
 * const { data } = await postMidasFlow<IntlCallbackResponse>({
 *   callflow: 'rapidsos_internal/international-callback',
 *   variables: { international_number: '+442071838750' },
 * });
 */
const postMidasFlow = <
  TResponse = unknown,
  TVariables = Record<string, unknown>,
>({
  callflow,
  variables,
}: PostMidasFlowParams<TVariables>): Promise<AxiosResponse<TResponse>> => {
  return midasInstance.post<TResponse>(
    '/internal_auth/trigger',
    { callflow, blocking: true, variables },
    { headers: { retry: true } },
  );
};

export default postMidasFlow;
